/**
 * One Platform Portal analytics taxonomy (ALP-32444).
 *
 * Every custom RUM event MUST be described by this file. The collector
 * (`portal_events_collector.py`) stores `event_name` as a top-level field and
 * `event_domain` under `meta.event_domain`, and the User Event Dashboard groups
 * on exactly those two. Ad-hoc names invented at the call site are what made the
 * Invoicing Portal data hard to query — a closed vocabulary is the whole point.
 *
 * Naming rules:
 *  - `event_domain` is the MODULE the user was in — one value per top-level
 *    route or feature area, kebab-case, matching the route segment where one
 *    exists so it lines up with `meta.page_group` derived from the URL.
 *  - `event_name` is the ACTION, `object.verb` in snake_case. It is deliberately
 *    module-agnostic so "how often is anything exported" stays answerable.
 *  - Attribute keys are snake_case: the collector's logfmt parser rewrites `-`
 *    and `:` to `_`, so anything else silently changes shape on the way in.
 */

/** Modules — the `event_domain` vocabulary. */
export const AnalyticsModule = {
  About: "about",
  AccessControlPanel: "access-control-panel",
  AirBooking: "air-booking",
  AirGateway: "air-gateway",
  AirSpotQuote: "air-spot-quote",
  ContainerUtilization: "container-utilization",
  CostReport: "cost-report",
  Csop: "csop",
  CargoCapacity: "consol-capacity",
  CargoCapacityConfig: "consol-capacity-data-global-config",
  CustomerBillingLcl: "customer-billing-lcl",
  Dashboard: "dashboard",
  DocumentCenter: "document-center",
  Email: "email",
  Genie: "genie",
  Home: "home",
  Insight: "insight",
  KunAssistant: "kun-assistant",
  Modules: "modules",
  LclBooking: "lcl-booking",
  Notification: "notification",
  PoManagement: "po-management",
  ProductRoadmap: "product-roadmap",
  QuickAccess: "quick-access",
  RagManagement: "rag-management",
  RatingSearch: "rating-search",
  Report: "report",
  Station: "station",
  TaxInvoice: "tax-invoice",
  TaxInvoiceLcl: "tax-invoice-lcl",
  Updates: "updates",
  UserEvents: "user-events",
  VolumeDashboard: "volume-dashboard",
  /** Chrome that is not owned by any one module: global nav, layout, shell. */
  Shell: "shell",
} as const;

export type AnalyticsModule = (typeof AnalyticsModule)[keyof typeof AnalyticsModule];

/** Actions — the `event_name` vocabulary. */
export const AnalyticsAction = {
  // Navigation & lifecycle
  PageView: "page.view",
  ModuleEnter: "module.enter",
  TabSwitch: "tab.switch",
  NavExternal: "nav.external",
  /**
   * The user navigated away before a route finished loading. Next.js reports this by throwing
   * `"Route Cancelled"` from `routeChangeError`, which is NOT a failure — it is a fast user. It
   * was previously recorded as `action.error`, and since nothing else ever emitted that name,
   * 100% of the "errors" on the dashboard were this. Counting it separately keeps `action.error`
   * meaning "something broke".
   */
  NavCancelled: "nav.cancelled",

  // Query
  SearchApply: "search.apply",
  FilterChange: "filter.change",
  FilterReset: "filter.reset",

  // Table
  TableSort: "table.sort",
  TablePaginate: "table.paginate",
  TableColumnConfig: "table.column_config",
  TableRowOpen: "table.row_open",
  TableRowSelect: "table.row_select",

  // Data in / out
  Export: "export.download",
  FileUpload: "file.upload",
  FileDownload: "file.download",
  FilePreview: "file.preview",

  // Business writes
  RecordCreate: "record.create",
  RecordUpdate: "record.update",
  RecordSubmit: "record.submit",
  RecordApprove: "record.approve",
  RecordReject: "record.reject",
  RecordDelete: "record.delete",

  // Generic UI interaction, emitted by the global click delegate rather than by a
  // call site. It is the safety net: a module nobody has hand-instrumented still
  // reports WHICH control people press. Hand-written events are more precise and
  // should be preferred where the action matters.
  UiClick: "ui.click",

  // Surfaces
  ModalOpen: "modal.open",
  ModalClose: "modal.close",
  DrawerOpen: "drawer.open",

  // AI surfaces
  AiPrompt: "ai.prompt",
  AiResponse: "ai.response",
  AiFeedback: "ai.feedback",

  /**
   * One backend call, emitted by the axios interceptor rather than by a call site — so every
   * request is covered without per-module work. Carries `target` (the request path with record
   * ids masked), `method`, `status`, `result` and `duration_ms`. This is the source for both the
   * API half of the error panel and the API half of response-time tracking; Faro's own fetch/XHR
   * instrumentation records the same thing but ships it to Tempo as a trace, which the Loki
   * collector never sees.
   */
  ApiCall: "api.call",

  // Outcomes worth counting on their own
  ActionError: "action.error",
  StateEmpty: "state.empty",
} as const;

export type AnalyticsAction = (typeof AnalyticsAction)[keyof typeof AnalyticsAction];

/** Outcome of an action, so success/failure ratios are queryable per event. */
export type AnalyticsResult = "success" | "failure" | "empty";

/**
 * Attributes accepted at a call site. Values are typed loosely on purpose —
 * Faro's own `EventAttributes` is `Record<string, string>`, and `track()` does
 * the stringification so no caller has to remember to.
 */
export interface AnalyticsAttributes {
  /** What was acted upon: a button id, column name, tab label, file type. */
  target?: string;
  /** Outcome, when the action can fail or come back empty. */
  result?: AnalyticsResult;
  /** Rows returned, files selected, items exported — anything countable. */
  count?: number;
  /** Client-observed duration of the action. */
  duration_ms?: number;
  /** Free-form extras; keys must stay snake_case. */
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Route segment -> module. Used to attribute a page view without every page
 * having to declare itself. Keys are the FIRST path segment, which is also what
 * the collector uses for `meta.page_group`, so the two agree by construction.
 */
const ROUTE_TO_MODULE: Record<string, AnalyticsModule> = {
  "": AnalyticsModule.Home,
  "access-control-panel": AnalyticsModule.AccessControlPanel,
  "air-booking": AnalyticsModule.AirBooking,
  "air-gateway": AnalyticsModule.AirGateway,
  "air-spot-quote": AnalyticsModule.AirSpotQuote,
  "container-utilization": AnalyticsModule.ContainerUtilization,
  "cost-report": AnalyticsModule.CostReport,
  "consol-capacity": AnalyticsModule.CargoCapacity,
  "consol-capacity-data-global-config": AnalyticsModule.CargoCapacityConfig,
  csop: AnalyticsModule.Csop,
  "customer-billing-lcl": AnalyticsModule.CustomerBillingLcl,
  dashboard: AnalyticsModule.Dashboard,
  "document-center": AnalyticsModule.DocumentCenter,
  "lcl-booking": AnalyticsModule.LclBooking,
  modules: AnalyticsModule.Modules,
  "po-management": AnalyticsModule.PoManagement,
  "product-roadmap": AnalyticsModule.ProductRoadmap,
  "quick-access": AnalyticsModule.QuickAccess,
  "rag-management": AnalyticsModule.RagManagement,
  RatingSearchPage: AnalyticsModule.RatingSearch,
  report: AnalyticsModule.Report,
  station: AnalyticsModule.Station,
  "tax-invoice": AnalyticsModule.TaxInvoice,
  "tax-invoice-lcl": AnalyticsModule.TaxInvoiceLcl,
  insight: AnalyticsModule.Insight,
  updates: AnalyticsModule.Updates,
  "user-events": AnalyticsModule.UserEvents,
  "volume-dashboard": AnalyticsModule.VolumeDashboard,
};

/**
 * Resolve a pathname to its module. Unknown routes fall back to `shell` rather
 * than inventing a domain, so the vocabulary stays closed and a missing mapping
 * shows up as a visible `shell` spike instead of silently fragmenting the data.
 */
export function moduleFromPath(pathname: string): AnalyticsModule {
  const segment = (pathname || "").split("?")[0].split("/").filter(Boolean)[0] ?? "";
  return ROUTE_TO_MODULE[segment] ?? AnalyticsModule.Shell;
}
